import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { 
  ChevronLeft, ChevronRight, ClipboardList, Plus, Eye, 
  Calendar, User, Send, Mail, MessageCircle, MessageSquare, Check, AlertCircle,
  Heart, Activity, Target, Dumbbell, Brain, Trash2,
  Mic, MicOff, Square, Loader2, Sparkles, CheckCircle, XCircle, Pause, Play
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Scale, Droplet } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Category icons and labels
const CATEGORY_CONFIG = {
  general_health: { icon: Heart, label: 'Allgemeine Gesundheit', color: 'bg-red-100 text-red-700' },
  vitals: { icon: Activity, label: 'Vitalwerte', color: 'bg-rose-100 text-rose-700' },
  body_data: { icon: Scale, label: 'Körperdaten', color: 'bg-cyan-100 text-cyan-700' },
  pain: { icon: AlertCircle, label: 'Schmerzen', color: 'bg-orange-100 text-orange-700' },
  mobility: { icon: Activity, label: 'Bewegungseinschränkungen', color: 'bg-yellow-100 text-yellow-700' },
  cardiovascular: { icon: Heart, label: 'Herz-Kreislauf', color: 'bg-pink-100 text-pink-700' },
  lifestyle: { icon: Brain, label: 'Lebensstil', color: 'bg-blue-100 text-blue-700' },
  goals: { icon: Target, label: 'Ziele', color: 'bg-green-100 text-green-700' }
};

const CustomerAnamnese = ({ customerId, customerName, onSave }) => {
  const [records, setRecords] = useState([]);
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [viewRecordOpen, setViewRecordOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [sendLinkDialogOpen, setSendLinkDialogOpen] = useState(false);
  
  // Wizard state
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [notes, setNotes] = useState('');
  const [conversationLog, setConversationLog] = useState(null);
  
  // Group questions by category
  const [categories, setCategories] = useState([]);

  // Voice anamnese state
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [voiceStep, setVoiceStep] = useState('record'); // 'record', 'processing', 'results'
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [voiceProcessing, setVoiceProcessing] = useState(false);
  const [voiceResults, setVoiceResults] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);

  // Live transcription state (Whisper only)
  const [transcriptLines, setTranscriptLines] = useState([]);
  const [intermediateSummary, setIntermediateSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [sessionId] = useState(() => crypto.randomUUID());
  const subtitleRef = useRef(null);
  const whisperChunkRef = useRef(null);
  const whisperRecorderRef = useRef(null);
  const isRecordingRef = useRef(false);
  const cacheIntervalRef = useRef(null);

  // Gespeicherte Gespräche (Transkript-Entwürfe für spätere Analyse)
  const [voiceDrafts, setVoiceDrafts] = useState([]);
  const [activeDraftId, setActiveDraftId] = useState(null);
  const [draftAnalyzingId, setDraftAnalyzingId] = useState(null);

  const fetchVoiceDrafts = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/anamnese/voice-drafts/${customerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVoiceDrafts(res.data.drafts || []);
    } catch { /* ignore */ }
  }, [customerId]);

  useEffect(() => { fetchVoiceDrafts(); }, [fetchVoiceDrafts]);

  const saveVoiceDraft = async (silent = false) => {
    if (!transcriptLines || transcriptLines.length === 0) {
      if (!silent) toast.error('Kein Transkript vorhanden — bitte zuerst aufnehmen');
      return false;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/anamnese/voice-drafts`, {
        customer_id: customerId,
        lines: transcriptLines,
        recording_seconds: recordingTime,
      }, { headers: { Authorization: `Bearer ${token}` } });
      fetchVoiceDrafts();
      if (!silent) {
        toast.success('Gespräch gespeichert — Analyse kann später durchgeführt werden');
        setVoiceOpen(false);
      }
      return true;
    } catch {
      if (!silent) toast.error('Speichern fehlgeschlagen');
      return false;
    }
  };

  const analyzeDraft = async (draftId) => {
    setDraftAnalyzingId(draftId);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/anamnese/voice-drafts/${draftId}/analyze`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000,
      });
      setVoiceResults(res.data);
      setActiveDraftId(draftId);
      setVoiceStep('results');
      setVoiceOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Analyse fehlgeschlagen — Gespräch bleibt gespeichert');
    }
    setDraftAnalyzingId(null);
  };

  const deleteDraft = async (draftId) => {
    if (!window.confirm('Gespeichertes Gespräch wirklich löschen?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/anamnese/voice-drafts/${draftId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchVoiceDrafts();
    } catch { toast.error('Löschen fehlgeschlagen'); }
  };

  const consumeActiveDraft = async () => {
    if (!activeDraftId) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/anamnese/voice-drafts/${activeDraftId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch { /* ignore */ }
    setActiveDraftId(null);
    fetchVoiceDrafts();
  };

  // Auto-scroll subtitles to bottom
  useEffect(() => {
    if (subtitleRef.current) {
      subtitleRef.current.scrollTop = subtitleRef.current.scrollHeight;
    }
  }, [transcriptLines]);

  // Auto-cache transcript to server every 15 seconds
  useEffect(() => {
    if (isRecording && transcriptLines.length > 0) {
      cacheIntervalRef.current = setInterval(async () => {
        try {
          const token = localStorage.getItem('token');
          await axios.post(`${API}/anamnese/voice-cache-transcript`, {
            session_id: sessionId,
            lines: transcriptLines.map((t, i) => ({ text: t, timestamp: Date.now(), index: i })),
            recording_seconds: recordingTime,
          }, { headers: { Authorization: `Bearer ${token}` }, timeout: 5000 });
        } catch {}
      }, 15000);
      return () => clearInterval(cacheIntervalRef.current);
    }
    return () => clearInterval(cacheIntervalRef.current);
  }, [isRecording, transcriptLines, sessionId, recordingTime]);
  const recordsCount = useMemo(() => records.length, [records]);
  const currentCategory = useMemo(() => categories[currentStep], [categories, currentStep]);



  const fetchRecords = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/anamnese/customer/${customerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecords(response.data);
    } catch (error) {
      console.error('Error fetching anamnese records:', error);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  const fetchTemplate = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/anamnese/templates/default`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplate(response.data);
      
      // Group questions by category
      const questions = response.data.questions || [];
      const grouped = {};
      questions.forEach(q => {
        if (!grouped[q.category]) {
          grouped[q.category] = [];
        }
        grouped[q.category].push(q);
      });
      
      // Sort questions within each category by order
      Object.keys(grouped).forEach(cat => {
        grouped[cat].sort((a, b) => a.order - b.order);
      });
      
      // Create ordered category list
      const categoryOrder = ['general_health', 'vitals', 'body_data', 'pain', 'mobility', 'cardiovascular', 'lifestyle', 'goals'];
      const orderedCategories = categoryOrder
        .filter(cat => grouped[cat] && grouped[cat].length > 0)
        .map(cat => ({
          key: cat,
          ...CATEGORY_CONFIG[cat],
          questions: grouped[cat]
        }));
      
      setCategories(orderedCategories);
    } catch (error) {
      console.error('Error fetching template:', error);
    }
  }, []);

  useEffect(() => {
    if (customerId) {
      fetchRecords();
      fetchTemplate();
    }
  }, [customerId, fetchRecords, fetchTemplate]);

  const startNewAnamnese = useCallback(() => {
    setAnswers({});
    setNotes('');
    setCurrentStep(0);
    setWizardOpen(true);
  }, []);

  // Voice Anamnese Functions
  const startVoiceAnamnese = () => {
    setVoiceOpen(true);
    setVoiceStep('record');
    setIsRecording(false);
    setIsPaused(false);
    setRecordingTime(0);
    setVoiceResults(null);
    setAudioBlob(null);
    setTranscriptLines([]);
    setIntermediateSummary(null);
    audioChunksRef.current = [];
    isRecordingRef.current = false;
  };

  const startWhisperChunkCycle = (stream) => {
    if (!isRecordingRef.current) return;
    
    try {
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') 
        ? 'audio/webm;codecs=opus' : 'audio/webm';
      const recorder = new MediaRecorder(stream, { mimeType });
      const chunks = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };

      recorder.onstop = async () => {
        if (chunks.length === 0 || !isRecordingRef.current) return;
        const blob = new Blob(chunks, { type: mimeType });

        try {
          const authToken = localStorage.getItem('token');
          const formData = new FormData();
          formData.append('audio', blob, 'chunk.webm');
          const res = await axios.post(`${API}/anamnese/voice-stream-chunk`, formData, {
            headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'multipart/form-data' },
            timeout: 15000,
          });
          const text = res.data?.text;
          if (text && text.trim()) {
            setTranscriptLines(prev => [...prev, text.trim()]);
          }
        } catch (err) {
          console.warn('Whisper chunk error:', err);
        }

        // Continue cycle if still recording
        if (isRecordingRef.current) {
          startWhisperChunkCycle(stream);
        }
      };

      whisperRecorderRef.current = recorder;
      recorder.start();
      
      // Stop after 5 seconds to send chunk
      whisperChunkRef.current = setTimeout(() => {
        if (recorder.state === 'recording') {
          recorder.stop();
        }
      }, 5000);
    } catch (err) {
      console.warn('Whisper chunk cycle error:', err);
    }
  };

  const stopWhisperStreaming = () => {
    if (whisperChunkRef.current) {
      clearTimeout(whisperChunkRef.current);
      whisperChunkRef.current = null;
    }
    if (whisperRecorderRef.current && whisperRecorderRef.current.state === 'recording') {
      try { whisperRecorderRef.current.stop(); } catch {}
    }
    whisperRecorderRef.current = null;
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') 
        ? 'audio/webm;codecs=opus' : 'audio/webm';
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        setAudioBlob(blob);
        stream.getTracks().forEach(t => t.stop());
      };

      mediaRecorder.start(1000);
      setIsRecording(true);
      setIsPaused(false);
      setTranscriptLines([]);
      isRecordingRef.current = true;
      timerRef.current = setInterval(() => setRecordingTime(p => p + 1), 1000);

      // Start Whisper live transcription on same stream
      startWhisperChunkCycle(stream);
    } catch (err) {
      toast.error('Mikrofon-Zugriff verweigert');
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      isRecordingRef.current = false;
      clearInterval(timerRef.current);
      stopWhisperStreaming();
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current?.state === 'paused') {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      isRecordingRef.current = true;
      timerRef.current = setInterval(() => setRecordingTime(p => p + 1), 1000);
      // Resume Whisper chunks using the same stream
      const stream = mediaRecorderRef.current.stream;
      startWhisperChunkCycle(stream);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      isRecordingRef.current = false;
      stopWhisperStreaming();
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      clearInterval(timerRef.current);
      // Final cache save
      const token = localStorage.getItem('token');
      if (transcriptLines.length > 0) {
        axios.post(`${API}/anamnese/voice-cache-transcript`, {
          session_id: sessionId,
          lines: transcriptLines.map((t, i) => ({ text: t, timestamp: Date.now(), index: i })),
          recording_seconds: recordingTime,
        }, { headers: { Authorization: `Bearer ${token}` }, timeout: 5000 }).catch(() => {});
      }
    }
  };

  const fetchIntermediateSummary = async () => {
    if (transcriptLines.length === 0) {
      toast.error('Noch keine Transkription vorhanden');
      return;
    }
    setSummaryLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/anamnese/voice-intermediate-summary`, {
        transcript_lines: transcriptLines,
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000,
      });
      setIntermediateSummary(res.data);
    } catch (err) {
      toast.error('Zwischenzusammenfassung fehlgeschlagen');
    }
    setSummaryLoading(false);
  };

  const cleanupCache = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/anamnese/voice-cache-transcript/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {}
  };

  const processVoiceRecording = async () => {
    if (!audioBlob) return;
    setVoiceStep('processing');
    setVoiceProcessing(true);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('audio', audioBlob, 'anamnese_recording.webm');

      const res = await axios.post(`${API}/anamnese/voice-analyze`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        timeout: 120000, // 2 min timeout for long recordings
      });

      setVoiceResults(res.data);
      setVoiceStep('results');
      cleanupCache(); // Cache nicht mehr nötig nach erfolgreicher Analyse
    } catch (err) {
      // Analyse fehlgeschlagen → Transkript automatisch beim Kunden sichern
      let saved = false;
      if (transcriptLines && transcriptLines.length > 0) {
        saved = await saveVoiceDraft(true);
      }
      toast.error(
        (err.response?.data?.detail || 'Analyse fehlgeschlagen') +
        (saved ? ' — Gespräch wurde gespeichert und kann später analysiert werden.' : ''),
        { duration: 8000 }
      );
      setVoiceStep('record');
    }
    setVoiceProcessing(false);
  };

  const applyVoiceResults = () => {
    if (!voiceResults?.answers) return;
    
    // Merge AI answers into the form answers
    const newAnswers = { ...answers };
    for (const [qId, value] of Object.entries(voiceResults.answers)) {
      if (value !== null && value !== undefined && value !== '') {
        newAnswers[qId] = value;
      }
    }
    setAnswers(newAnswers);

    // Add summary as notes
    if (voiceResults.summary) {
      setNotes(prev => prev ? `${prev}\n\n[KI-Anamnese] ${voiceResults.summary}` : `[KI-Anamnese] ${voiceResults.summary}`);
    }

    // Save conversation log (from intermediate summary or voice results dialog)
    const dialog = intermediateSummary?.dialog || voiceResults.dialog || [];
    if (dialog.length > 0) {
      setConversationLog(dialog);
    }

    setVoiceOpen(false);
    setWizardOpen(true);
    setCurrentStep(0);
    toast.success(`${Object.keys(voiceResults.answers).length} Felder aus Sprachaufnahme uebernommen`);
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  const handleAnswer = useCallback((questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  }, []);

  const handleMultiChoice = useCallback((questionId, option, checked) => {
    setAnswers(prev => {
      const current = prev[questionId] || [];
      if (checked) {
        return { ...prev, [questionId]: [...current, option] };
      }
      return { ...prev, [questionId]: current.filter(o => o !== option) };
    });
  }, []);

  const nextStep = useCallback(() => {
    if (currentStep < categories.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  }, [categories.length, currentStep]);

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const submitAnamnese = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Validate customerId
      if (!customerId) {
        toast.error('Keine Kunden-ID gefunden');
        return;
      }
      
      // Validate required fields
      const allQuestions = categories.flatMap(cat => cat.questions);
      const missingRequired = [];
      
      for (const question of allQuestions) {
        if (question.required) {
          const answer = answers[question.id];
          const isEmpty = answer === undefined || answer === null || answer === '' || 
            (Array.isArray(answer) && answer.length === 0);
          
          if (isEmpty) {
            missingRequired.push({
              category: question.category,
              question: question.question_text
            });
          }
        }
      }
      
      if (missingRequired.length > 0) {
        // Find the first missing category to navigate to
        const firstMissing = missingRequired[0];
        const categoryIndex = categories.findIndex(cat => cat.key === firstMissing.category);
        if (categoryIndex >= 0) {
          setCurrentStep(categoryIndex);
        }
        
        toast.error(`Bitte alle Pflichtfelder ausfüllen. Es fehlen ${missingRequired.length} Angaben.`);
        console.log('Missing required fields:', missingRequired);
        return;
      }
      
      // Convert answers to array format
      const answersArray = Object.entries(answers).map(([questionId, answer]) => ({
        question_id: questionId,
        answer: answer
      }));
      
      console.log('Submitting anamnese:', {
        customer_id: customerId,
        template_id: template?.id,
        answers: answersArray.length
      });
      
      await axios.post(`${API}/anamnese/records`, {
        customer_id: customerId,
        template_id: template.id,
        answers: answersArray,
        notes: notes,
        conversation_log: conversationLog
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Anamnese erfolgreich gespeichert');
      setWizardOpen(false);
      fetchRecords();
      if (onSave) onSave();
    } catch (error) {
      console.error('Error saving anamnese:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern der Anamnese');
    }
  }, [answers, categories, customerId, fetchRecords, notes, template]);

  const deleteRecord = useCallback(async (recordId) => {
    if (!window.confirm('Anamnese wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/anamnese/records/${recordId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Anamnese gelöscht');
      setViewRecordOpen(false);
      setSelectedRecord(null);
      fetchRecords();
      if (onSave) onSave();
    } catch (error) {
      console.error('Error deleting anamnese:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  }, [fetchRecords, onSave]);

  const viewRecord = useCallback((record) => {
    setSelectedRecord(record);
    setViewRecordOpen(true);
  }, []);

  const sendAnamneseLink = useCallback(async (method) => {
    try {
      const token = localStorage.getItem('token');
      
      // Create public link
      const response = await axios.post(
        `${API}/anamnese/public-link?customer_id=${customerId}&template_id=default&expires_in_days=7`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const link = `${window.location.origin}/anamnese/fill/${response.data.token}`;
      
      if (method === 'copy') {
        await navigator.clipboard.writeText(link);
        toast.success('Link in Zwischenablage kopiert!');
      } else if (method === 'whatsapp') {
        const message = encodeURIComponent(`Hallo ${customerName},\n\nbitte füllen Sie vor Ihrem ersten Termin unsere Anamnese aus:\n\n${link}\n\nDer Link ist 7 Tage gültig.\n\nVielen Dank!`);
        window.open(`https://wa.me/?text=${message}`, '_blank');
        toast.success('WhatsApp geöffnet');
      } else if (method === 'email') {
        const subject = encodeURIComponent('Ihre Anamnese - Bitte ausfüllen');
        const body = encodeURIComponent(`Hallo ${customerName},\n\nbitte füllen Sie vor Ihrem ersten Termin unsere Anamnese aus:\n\n${link}\n\nDer Link ist 7 Tage gültig.\n\nVielen Dank!\nIhr Studio-Team`);
        window.location.href = `mailto:?subject=${subject}&body=${body}`;
        toast.success('E-Mail-Programm geöffnet');
      }
      
      setSendLinkDialogOpen(false);
    } catch (error) {
      console.error('Error creating link:', error);
      toast.error('Fehler beim Erstellen des Links');
    }
  }, [customerId, customerName]);

  const renderQuestion = useCallback((question) => {
    const value = answers[question.id];
    
    switch (question.question_type) {
      case 'yes_no':
        return (
          <RadioGroup
            value={value || ''}
            onValueChange={(val) => handleAnswer(question.id, val)}
            className="flex gap-4"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="ja" id={`${question.id}-ja`} data-testid={`anamnese-question-${question.id}-yes`} />
              <Label htmlFor={`${question.id}-ja`}>Ja</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="nein" id={`${question.id}-nein`} data-testid={`anamnese-question-${question.id}-no`} />
              <Label htmlFor={`${question.id}-nein`}>Nein</Label>
            </div>
          </RadioGroup>
        );
      
      case 'text':
        return (
          <Input
            value={value || ''}
            onChange={(e) => handleAnswer(question.id, e.target.value)}
            placeholder="Ihre Antwort..."
            data-testid={`anamnese-question-${question.id}-input`}
          />
        );
      
      case 'textarea':
        return (
          <Textarea
            value={value || ''}
            onChange={(e) => handleAnswer(question.id, e.target.value)}
            placeholder="Ihre Antwort..."
            rows={3}
            data-testid={`anamnese-question-${question.id}-textarea`}
          />
        );
      
      case 'number':
        return (
          <Input
            type="number"
            value={value || ''}
            onChange={(e) => handleAnswer(question.id, e.target.value)}
            placeholder="0"
            data-testid={`anamnese-question-${question.id}-number`}
          />
        );
      
      case 'scale':
        return (
          <div className="space-y-3" data-testid={`anamnese-question-${question.id}-scale`}>
            <div className="flex justify-between text-sm text-gray-500">
              <span>1 (niedrig)</span>
              <span>10 (hoch)</span>
            </div>
            <Slider
              value={[value || 5]}
              onValueChange={([val]) => handleAnswer(question.id, val)}
              min={1}
              max={10}
              step={1}
              className="w-full"
              data-testid={`anamnese-question-${question.id}-slider`}
            />
            <div className="text-center text-2xl font-bold text-blue-600" data-testid={`anamnese-question-${question.id}-slider-value`}>
              {value || 5}
            </div>
          </div>
        );
      
      case 'single_choice':
        return (
          <RadioGroup
            value={value || ''}
            onValueChange={(val) => handleAnswer(question.id, val)}
            className="space-y-2"
          >
            {question.options?.map((option, idx) => (
              <div key={idx} className="flex items-center space-x-2">
                <RadioGroupItem value={option} id={`${question.id}-${idx}`} data-testid={`anamnese-question-${question.id}-option-${idx}`} />
                <Label htmlFor={`${question.id}-${idx}`} className="cursor-pointer">{option}</Label>
              </div>
            ))}
          </RadioGroup>
        );
      
      case 'multi_choice':
        return (
          <div className="space-y-2">
            {question.options?.map((option, idx) => (
              <div key={idx} className="flex items-center space-x-2">
                <Checkbox
                  id={`${question.id}-${idx}`}
                  checked={(value || []).includes(option)}
                  onCheckedChange={(checked) => handleMultiChoice(question.id, option, checked)}
                  data-testid={`anamnese-question-${question.id}-multi-${idx}`}
                />
                <Label htmlFor={`${question.id}-${idx}`} className="cursor-pointer">{option}</Label>
              </div>
            ))}
          </div>
        );
      
      case 'date':
        return (
          <Input
            type="date"
            value={value || ''}
            onChange={(e) => handleAnswer(question.id, e.target.value)}
            data-testid={`anamnese-question-${question.id}-date`}
          />
        );
      
      default:
        return (
          <Input
            value={value || ''}
            onChange={(e) => handleAnswer(question.id, e.target.value)}
            placeholder="Ihre Antwort..."
            data-testid={`anamnese-question-${question.id}-default`}
          />
        );
    }
  }, [answers, handleAnswer, handleMultiChoice]);

  const formatAnswerForDisplay = useCallback((answer) => {
    if (Array.isArray(answer)) {
      return answer.join(', ');
    }
    return answer?.toString() || '-';
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48" data-testid="anamnese-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" data-testid="anamnese-loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="customer-anamnese-tab">
      {/* Header */}
      <div className="flex items-center justify-between" data-testid="anamnese-header">
        <div className="flex items-center gap-2">
          <ClipboardList className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-semibold" data-testid="anamnese-title">Anamnese</h3>
          <Badge variant="outline" data-testid="anamnese-record-count">{recordsCount} Einträge</Badge>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setSendLinkDialogOpen(true)} data-testid="anamnese-send-link-button">
            <Send className="h-4 w-4 mr-2" />
            Link senden
          </Button>
          <Button variant="outline" size="sm" onClick={startVoiceAnamnese} className="border-purple-300 text-purple-700 hover:bg-purple-50" data-testid="anamnese-voice-button">
            <Mic className="h-4 w-4 mr-2" />
            Per Sprache
          </Button>
          <Button size="sm" onClick={startNewAnamnese} data-testid="anamnese-new-button">
            <Plus className="h-4 w-4 mr-2" />
            Neue Anamnese
          </Button>
        </div>
      </div>

      {/* Gespeicherte Gespräche (ausstehende Analyse) */}
      {voiceDrafts.length > 0 && (
        <Card className="border-amber-300 bg-amber-50/60" data-testid="anamnese-voice-drafts">
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center gap-2 text-amber-800 font-medium text-sm">
              <Mic className="h-4 w-4" />
              Gespeicherte Gespräche ({voiceDrafts.length}) — Analyse ausstehend
            </div>
            {voiceDrafts.map((d) => (
              <div key={d.id} className="flex items-center justify-between bg-white rounded-lg border border-amber-200 px-3 py-2" data-testid={`voice-draft-${d.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium">
                    Gespräch vom {new Date(d.created_at).toLocaleString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </p>
                  <p className="text-xs text-gray-500 truncate max-w-md">
                    {d.lines?.length || 0} Abschnitte{d.recording_seconds ? ` · ${Math.round(d.recording_seconds / 60)} Min.` : ''} — „{(d.lines?.[0] || '').slice(0, 60)}…“
                  </p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button
                    size="sm"
                    onClick={() => analyzeDraft(d.id)}
                    disabled={draftAnalyzingId === d.id}
                    className="bg-purple-600 hover:bg-purple-700"
                    data-testid={`voice-draft-${d.id}-analyze`}
                  >
                    {draftAnalyzingId === d.id ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
                    Jetzt analysieren
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:bg-red-50"
                    onClick={() => deleteDraft(d.id)}
                    data-testid={`voice-draft-${d.id}-delete`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Records List */}
      {records.length === 0 ? (
        <Card data-testid="anamnese-empty-state">
          <CardContent className="p-8 text-center text-gray-500" data-testid="anamnese-empty-content">
            <ClipboardList className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>Noch keine Anamnese vorhanden.</p>
            <p className="text-sm mt-2">Erstellen Sie eine neue Anamnese oder senden Sie dem Kunden einen Link zum Ausfüllen.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {records.map((record) => (
            <Card key={record.id} className="hover:shadow-md transition-shadow" data-testid={`anamnese-record-${record.id}`}>
              <CardContent className="p-4" data-testid={`anamnese-record-${record.id}-content`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 cursor-pointer flex-1" onClick={() => viewRecord(record)} data-testid={`anamnese-record-${record.id}-open`}>
                    <div className="bg-blue-100 p-2 rounded-full">
                      <ClipboardList className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium">
                        Anamnese vom {new Date(record.created_at).toLocaleDateString('de-CH')}
                      </p>
                      <p className="text-sm text-gray-500">
                        Ausgefüllt von: {record.filled_by_name} ({record.filled_by_type === 'trainer' ? 'Trainer' : 'Kunde'})
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => viewRecord(record)} data-testid={`anamnese-record-${record.id}-view`}>
                      <Eye className="h-4 w-4 mr-2" />
                      Ansehen
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteRecord(record.id);
                      }}
                      data-testid={`anamnese-record-${record.id}-delete`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Wizard Dialog */}
      <Dialog open={wizardOpen} onOpenChange={setWizardOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="anamnese-wizard-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" data-testid="anamnese-wizard-title">
              <ClipboardList className="h-5 w-5" />
              Neue Anamnese - {customerName}
            </DialogTitle>
          </DialogHeader>
          
          {categories.length > 0 && (
            <>
              {/* Progress Steps */}
              <div className="flex items-center justify-between mb-6">
                {categories.map((cat, idx) => {
                  const Icon = cat.icon;
                  return (
                    <div 
                      key={cat.key}
                      className={`flex flex-col items-center cursor-pointer ${idx <= currentStep ? 'text-blue-600' : 'text-gray-400'}`}
                      onClick={() => setCurrentStep(idx)}
                      data-testid={`anamnese-step-${cat.key}`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-1 ${
                        idx < currentStep ? 'bg-blue-600 text-white' :
                        idx === currentStep ? 'bg-blue-100 text-blue-600 ring-2 ring-blue-600' :
                        'bg-gray-100'
                      }`}>
                        {idx < currentStep ? <Check className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                      </div>
                      <span className="text-xs hidden sm:block">{cat.label.split(' ')[0]}</span>
                    </div>
                  );
                })}
              </div>

              {!currentCategory ? (
                <div className="text-sm text-gray-500" data-testid="anamnese-template-missing">
                  Vorlage konnte nicht geladen werden.
                </div>
              ) : (
                <>
                  {/* Current Category */}
                  <div className="space-y-6">
                    <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${currentCategory.color}`} data-testid="anamnese-current-category">
                      {React.createElement(currentCategory.icon, { className: "h-4 w-4" })}
                      <span className="font-medium">{currentCategory.label}</span>
                    </div>

                    {/* Questions */}
                    <div className="space-y-6">
                      {currentCategory.questions.map((question) => {
                        const answer = answers[question.id];
                        const isEmpty = answer === undefined || answer === null || answer === '' || 
                          (Array.isArray(answer) && answer.length === 0);
                        const showError = question.required && isEmpty;
                        
                        return (
                          <div key={question.id} className={`space-y-2 p-3 rounded-lg ${showError ? 'bg-red-50 border border-red-200' : ''}`} data-testid={`anamnese-question-${question.id}`}>
                            <Label className="text-base font-medium">
                              {question.question_text}
                              {question.required && <span className="text-red-500 ml-1">*</span>}
                            </Label>
                            {question.help_text && (
                              <p className="text-sm text-gray-500">{question.help_text}</p>
                            )}
                            {renderQuestion(question)}
                            {showError && (
                              <p className="text-sm text-red-500 flex items-center gap-1" data-testid={`anamnese-question-${question.id}-error`}>
                                <AlertCircle className="h-3 w-3" />
                                Pflichtfeld
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Notes on last step */}
                    {currentStep === categories.length - 1 && (
                      <div className="space-y-2 pt-4 border-t" data-testid="anamnese-notes-section">
                        <Label className="text-base font-medium">Zusätzliche Notizen</Label>
                        <Textarea
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          placeholder="Weitere Anmerkungen des Trainers..."
                          rows={3}
                          data-testid="anamnese-notes-input"
                        />
                      </div>
                    )}
                  </div>

                  {/* Navigation */}
                  <DialogFooter className="flex justify-between mt-6">
                    <Button
                      variant="outline"
                      onClick={prevStep}
                      disabled={currentStep === 0}
                      data-testid="anamnese-back-button"
                    >
                      <ChevronLeft className="h-4 w-4 mr-2" />
                      Zurück
                    </Button>

                    {currentStep < categories.length - 1 ? (
                      <Button onClick={nextStep} data-testid="anamnese-next-button">
                        Weiter
                        <ChevronRight className="h-4 w-4 ml-2" />
                      </Button>
                    ) : (
                      <Button
                        onClick={submitAnamnese}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid="anamnese-save-button"
                      >
                        <Check className="h-4 w-4 mr-2" />
                        Anamnese speichern
                      </Button>
                    )}
                  </DialogFooter>
                </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* View Record Dialog */}
      <Dialog open={viewRecordOpen} onOpenChange={setViewRecordOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="anamnese-view-dialog">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle data-testid="anamnese-view-title">
                Anamnese vom {selectedRecord && new Date(selectedRecord.created_at).toLocaleDateString('de-CH')}
              </DialogTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={() => selectedRecord && deleteRecord(selectedRecord.id)}
                data-testid="anamnese-view-delete-button"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Löschen
              </Button>
            </div>
          </DialogHeader>
          
          {selectedRecord && (
            <div className="space-y-6">
                                                                                                                                                                                                                                                                                                                                                                            <div className="flex items-center gap-4 text-sm text-gray-500 bg-gray-50 p-3 rounded-lg" data-testid="anamnese-view-summary">
                <div className="flex items-center gap-2" data-testid="anamnese-view-filled-by">
                  <User className="h-4 w-4" />
                  <span>Ausgefüllt von: {selectedRecord.filled_by_name}</span>
                </div>
                <div className="flex items-center gap-2" data-testid="anamnese-view-date">
                  <Calendar className="h-4 w-4" />
                  <span>{new Date(selectedRecord.created_at).toLocaleString('de-CH')}</span>
                </div>
              </div>

              {/* Group answers by category */}
              {Object.entries(CATEGORY_CONFIG).map(([catKey, catConfig]) => {
                const categoryAnswers = selectedRecord.answers.filter(a => {
                  // Find question in template to get category
                  const question = template?.questions?.find(q => q.id === a.question_id);
                  return question?.category === catKey;
                });
                
                if (categoryAnswers.length === 0) return null;
                
                const Icon = catConfig.icon;
                
                return (
                  <div key={catKey} className="space-y-3">
                    <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${catConfig.color}`}>
                      <Icon className="h-4 w-4" />
                      <span className="font-medium">{catConfig.label}</span>
                    </div>
                    
                    <div className="space-y-2 pl-2">
                      {categoryAnswers.map((answer, idx) => (
                        <div key={idx} className="border-l-2 border-gray-200 pl-3 py-1">
                          <p className="text-sm text-gray-600">{answer.question_text}</p>
                          <p className="font-medium">{formatAnswerForDisplay(answer.answer)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}

              {/* Gesprächsprotokoll */}
              {selectedRecord.conversation_log && selectedRecord.conversation_log.length > 0 && (
                <div className="space-y-2 pt-4 border-t" data-testid="anamnese-conversation-log">
                  <Label className="font-medium flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    Gesprächsprotokoll
                  </Label>
                  <div className="bg-gray-50 rounded-lg p-3 space-y-2 max-h-64 overflow-y-auto">
                    {selectedRecord.conversation_log.map((entry, i) => (
                      <div key={i} className={`flex gap-2 items-start ${entry.speaker === 'trainer' ? '' : 'flex-row-reverse'}`}>
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0 ${
                          entry.speaker === 'trainer' 
                            ? 'bg-blue-100 text-blue-700' 
                            : 'bg-green-100 text-green-700'
                        }`}>
                          {entry.speaker === 'trainer' ? 'Trainer' : 'Kunde'}
                        </span>
                        <p className={`text-sm ${entry.speaker === 'trainer' ? 'text-blue-900' : 'text-green-900'}`}>
                          {entry.text}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedRecord.notes && (
                <div className="space-y-2 pt-4 border-t">
                  <Label className="font-medium">Zusätzliche Notizen</Label>
                  <p className="text-gray-700 bg-yellow-50 p-3 rounded-lg">{selectedRecord.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Send Link Dialog */}
      <Dialog open={sendLinkDialogOpen} onOpenChange={setSendLinkDialogOpen}>
        <DialogContent data-testid="anamnese-send-link-dialog">
          <DialogHeader>
            <DialogTitle data-testid="anamnese-send-link-title">Anamnese-Link senden</DialogTitle>
          </DialogHeader>

          <p className="text-sm text-gray-500 mb-4" data-testid="anamnese-send-link-description">
            Senden Sie dem Kunden einen Link, damit er die Anamnese selbst ausfüllen kann. Der Link ist 7 Tage gültig.
          </p>

          <div className="grid grid-cols-1 gap-3" data-testid="anamnese-send-link-options">
            <Button
              variant="outline"
              className="justify-start h-14"
              onClick={() => sendAnamneseLink('whatsapp')}
              data-testid="anamnese-send-link-whatsapp"
            >
              <MessageCircle className="h-5 w-5 mr-3 text-green-600" />
              <div className="text-left">
                <div className="font-medium">WhatsApp</div>
                <div className="text-xs text-gray-500">Link per WhatsApp senden</div>
              </div>
            </Button>

            <Button
              variant="outline"
              className="justify-start h-14"
              onClick={() => sendAnamneseLink('email')}
              data-testid="anamnese-send-link-email"
            >
              <Mail className="h-5 w-5 mr-3 text-blue-600" />
              <div className="text-left">
                <div className="font-medium">E-Mail</div>
                <div className="text-xs text-gray-500">Link per E-Mail senden</div>
              </div>
            </Button>

            <Button
              variant="outline"
              className="justify-start h-14"
              onClick={() => sendAnamneseLink('copy')}
              data-testid="anamnese-send-link-copy"
            >
              <ClipboardList className="h-5 w-5 mr-3 text-gray-600" />
              <div className="text-left">
                <div className="font-medium">Link kopieren</div>
                <div className="text-xs text-gray-500">Link in Zwischenablage kopieren</div>
              </div>
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Voice Anamnese Dialog */}
      <Dialog open={voiceOpen} onOpenChange={(open) => {
        if (!open && isRecording) stopRecording();
        setVoiceOpen(open);
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="anamnese-voice-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mic className="h-5 w-5 text-purple-600" />
              Anamnese per Sprache
            </DialogTitle>
          </DialogHeader>

          {/* Recording Step */}
          {voiceStep === 'record' && (
            <div className="space-y-4 py-4">
              <p className="text-sm text-gray-600">
                Legen Sie das iPad hin und fuehren Sie das Anamnesegespräch. 
                Die KI erkennt automatisch medizinisch relevante Informationen 
                und ignoriert Small Talk.
              </p>

              {/* Timer */}
              <div className="text-center py-4">
                <div className={`text-5xl font-mono font-bold ${isRecording && !isPaused ? 'text-red-600' : 'text-gray-400'}`} data-testid="anamnese-voice-timer">
                  {formatTime(recordingTime)}
                </div>
                {isRecording && (
                  <div className="flex items-center justify-center gap-2 mt-2">
                    <div className={`h-2 w-2 rounded-full ${isPaused ? 'bg-yellow-500' : 'bg-red-500 animate-pulse'}`} />
                    <span className="text-sm text-gray-500">{isPaused ? 'Pausiert' : 'Aufnahme laeuft...'}</span>
                  </div>
                )}
              </div>

              {/* Live Subtitles (Whisper) */}
              {(isRecording || transcriptLines.length > 0) && (
                <div
                  ref={subtitleRef}
                  className="bg-gray-900 rounded-lg p-3 max-h-48 overflow-y-auto scroll-smooth"
                  data-testid="anamnese-live-subtitles"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`h-1.5 w-1.5 rounded-full ${isRecording && !isPaused ? 'bg-green-500 animate-pulse' : 'bg-gray-500'}`} />
                    <span className="text-xs text-gray-400 font-medium">
                      Live-Transkription (Whisper)
                    </span>
                  </div>
                  <div className="space-y-1">
                    {transcriptLines.map((line, i) => (
                      <p key={i} className="text-sm text-white/80">{line}</p>
                    ))}
                    {isRecording && !isPaused && transcriptLines.length === 0 && (
                      <p className="text-xs text-gray-500 italic">
                        Transkribiere alle 5 Sekunden...
                      </p>
                    )}
                    {isRecording && !isPaused && transcriptLines.length > 0 && (
                      <p className="text-xs text-gray-600 italic animate-pulse">...</p>
                    )}
                  </div>
                </div>
              )}

              {/* Zwischenstand Button */}
              {transcriptLines.length >= 2 && (
                <div className="space-y-3">
                  <Button
                    onClick={fetchIntermediateSummary}
                    disabled={summaryLoading}
                    variant="outline"
                    size="sm"
                    className="w-full border-purple-300 text-purple-700 hover:bg-purple-50"
                    data-testid="anamnese-intermediate-summary-btn"
                  >
                    {summaryLoading ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Analysiere...</>
                    ) : (
                      <><Sparkles className="h-4 w-4 mr-2" /> Zwischenstand anzeigen</>
                    )}
                  </Button>

                  {/* Intermediate Summary Display */}
                  {intermediateSummary && intermediateSummary.success && (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-purple-800">Zwischenstand</span>
                        <Badge variant="outline" className="text-xs">
                          {intermediateSummary.fields_found} Felder erkannt
                        </Badge>
                      </div>

                      {/* Speaker Dialog */}
                      {intermediateSummary.dialog?.length > 0 && (
                        <div className="space-y-1.5 max-h-32 overflow-y-auto">
                          {intermediateSummary.dialog.map((d, i) => (
                            <div key={i} className={`flex gap-2 items-start ${d.speaker === 'trainer' ? '' : 'flex-row-reverse'}`}>
                              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0 ${
                                d.speaker === 'trainer' 
                                  ? 'bg-blue-100 text-blue-700' 
                                  : 'bg-green-100 text-green-700'
                              }`}>
                                {d.speaker === 'trainer' ? 'T' : 'K'}
                              </span>
                              <p className={`text-xs ${d.speaker === 'trainer' ? 'text-blue-900' : 'text-green-900'}`}>
                                {d.text}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}

                      <p className="text-xs text-purple-700 italic">{intermediateSummary.summary}</p>

                      {intermediateSummary.fields_missing?.length > 0 && (
                        <p className="text-[10px] text-gray-500">
                          Noch offen: {intermediateSummary.fields_missing.slice(0, 6).join(', ')}
                          {intermediateSummary.fields_missing.length > 6 && ` +${intermediateSummary.fields_missing.length - 6}`}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Controls */}
              <div className="flex justify-center gap-3">
                {!isRecording && !audioBlob && (
                  <Button 
                    onClick={startRecording} 
                    size="lg" 
                    className="bg-red-600 hover:bg-red-700 h-14 px-8 rounded-full"
                    data-testid="anamnese-voice-start-btn"
                  >
                    <Mic className="h-5 w-5 mr-2" />
                    Aufnahme starten
                  </Button>
                )}

                {isRecording && (
                  <>
                    <Button
                      onClick={isPaused ? resumeRecording : pauseRecording}
                      variant="outline"
                      size="lg"
                      className="h-14 px-6 rounded-full"
                      data-testid="anamnese-voice-pause-btn"
                    >
                      {isPaused ? <Play className="h-5 w-5 mr-2" /> : <Pause className="h-5 w-5 mr-2" />}
                      {isPaused ? 'Weiter' : 'Pause'}
                    </Button>
                    <Button
                      onClick={stopRecording}
                      size="lg"
                      className="bg-gray-800 hover:bg-gray-900 h-14 px-6 rounded-full"
                      data-testid="anamnese-voice-stop-btn"
                    >
                      <Square className="h-4 w-4 mr-2" />
                      Stopp
                    </Button>
                  </>
                )}

                {!isRecording && audioBlob && (
                  <>
                    <Button
                      onClick={startRecording}
                      variant="outline"
                      size="lg"
                      className="h-14 px-6 rounded-full"
                    >
                      <Mic className="h-5 w-5 mr-2" />
                      Neu aufnehmen
                    </Button>
                    <Button
                      onClick={() => saveVoiceDraft(false)}
                      variant="outline"
                      size="lg"
                      className="h-14 px-6 rounded-full border-amber-300 text-amber-700 hover:bg-amber-50"
                      data-testid="anamnese-voice-save-draft-btn"
                    >
                      <ClipboardList className="h-5 w-5 mr-2" />
                      Gespräch speichern
                    </Button>
                    <Button
                      onClick={processVoiceRecording}
                      size="lg"
                      className="bg-purple-600 hover:bg-purple-700 h-14 px-8 rounded-full"
                      data-testid="anamnese-voice-analyze-btn"
                    >
                      <Sparkles className="h-5 w-5 mr-2" />
                      Analysieren
                    </Button>
                  </>
                )}
              </div>

              {recordingTime > 0 && !isRecording && audioBlob && (
                <p className="text-center text-xs text-gray-400">
                  {formatTime(recordingTime)} aufgenommen - Unbegrenzte Länge möglich
                </p>
              )}
            </div>
          )}

          {/* Processing Step */}
          {voiceStep === 'processing' && (
            <div className="py-8 text-center space-y-4">
              <Loader2 className="h-12 w-12 mx-auto animate-spin text-purple-600" />
              <div>
                <p className="font-medium">KI analysiert das Gespräch...</p>
                <p className="text-sm text-gray-500">Transkription und medizinische Analyse</p>
              </div>
            </div>
          )}

          {/* Results Step */}
          {voiceStep === 'results' && voiceResults && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              {/* Confidence Badge */}
              <div className="flex items-center justify-between">
                <Badge className={voiceResults.confidence >= 0.7 ? 'bg-green-100 text-green-700' : voiceResults.confidence >= 0.4 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}>
                  Konfidenz: {Math.round(voiceResults.confidence * 100)}%
                </Badge>
                <span className="text-xs text-gray-400">
                  {Object.keys(voiceResults.answers).length} Felder erkannt
                </span>
              </div>

              {/* Summary */}
              {voiceResults.summary && (
                <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                  <p className="text-sm font-medium text-purple-800 mb-1">Zusammenfassung</p>
                  <p className="text-sm text-purple-700">{voiceResults.summary}</p>
                </div>
              )}

              {/* Extracted answers grouped by category */}
              {Object.keys(voiceResults.answers).length > 0 && (
                <div className="space-y-3">
                  <p className="text-sm font-medium">Erkannte Anamnese-Daten:</p>
                  {categories.map(cat => {
                    const catAnswers = cat.questions.filter(q => voiceResults.answers[q.id] !== undefined);
                    if (catAnswers.length === 0) return null;
                    const CatIcon = cat.icon;
                    return (
                      <div key={cat.key} className="bg-gray-50 rounded-lg p-3 border">
                        <div className="flex items-center gap-2 mb-2">
                          <CatIcon className="h-4 w-4 text-gray-600" />
                          <span className="text-sm font-medium">{cat.label}</span>
                        </div>
                        {catAnswers.map(q => (
                          <div key={q.id} className="flex items-start justify-between py-1 border-t border-gray-100">
                            <p className="text-xs text-gray-600 flex-1 pr-2">{q.question_text}</p>
                            <p className="text-xs font-medium text-right max-w-[50%]">
                              {Array.isArray(voiceResults.answers[q.id]) ? voiceResults.answers[q.id].join(', ') : String(voiceResults.answers[q.id])}
                            </p>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Ignored Topics */}
              {voiceResults.ignored_topics?.length > 0 && (
                <div className="text-xs text-gray-400">
                  Ignoriert (kein Anamnese-Bezug): {voiceResults.ignored_topics.join(', ')}
                </div>
              )}

              {/* Transcription (collapsible) */}
              <details className="text-xs">
                <summary className="text-gray-500 cursor-pointer">Transkription anzeigen</summary>
                <p className="mt-2 p-2 bg-gray-50 rounded text-gray-600 max-h-32 overflow-y-auto">
                  {voiceResults.transcription}
                </p>
              </details>

              {/* Actions */}
              <div className="flex gap-2 pt-2 border-t">
                <Button 
                  variant="outline" 
                  onClick={() => { setVoiceStep('record'); setAudioBlob(null); setRecordingTime(0); }}
                  className="flex-1"
                >
                  <Mic className="h-4 w-4 mr-2" />
                  Nochmal aufnehmen
                </Button>
                <Button 
                  onClick={() => { applyVoiceResults(); consumeActiveDraft(); }}
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                  disabled={Object.keys(voiceResults.answers).length === 0}
                  data-testid="anamnese-voice-apply-btn"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  In Formular uebernehmen ({Object.keys(voiceResults.answers).length})
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerAnamnese;
